/**
 * Base64 encoding adds ~33% overhead (4 chars per 3 bytes).
 * Use this to compute max blob size so the encoded payload stays under a limit.
 */
export const base64BlobLimit = (maxPayloadBytes: number) =>
  Math.floor((maxPayloadBytes * 3) / 4);

/**
 * Resizes/compresses an image blob to fit under maxSizeBytes.
 * Uses canvas to scale down and compress (JPEG) until under the limit.
 * If even lowest quality doesn't fit, scales down dimensions and retries.
 */
export async function resizeImageToMaxSize(blob: Blob, maxSizeBytes: number): Promise<Blob> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Error reading file."));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(blob);
  });

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not load image."));
    image.src = dataUrl;
  });

  const qualities = [0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2];
  let width = img.naturalWidth;
  let height = img.naturalHeight;
  const maxDimension = 4096;

  while (true) {
    if (width > maxDimension || height > maxDimension) {
      const scale = Math.min(maxDimension / width, maxDimension / height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    for (const quality of qualities) {
      const result = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), "image/jpeg", quality);
      });
      if (result && result.size <= maxSizeBytes) return result;
    }

    const lastResult = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.1);
    });
    if (lastResult && lastResult.size <= maxSizeBytes) return lastResult;

    if (width <= 256 || height <= 256) return lastResult ?? blob;

    width = Math.round(width * 0.6);
    height = Math.round(height * 0.6);
  }
}
